/* src/components/game/GameHeader.js */

import React from 'react';
import FocusMeter from '../dashboard/FocusMeter'; // Using the unified FocusMeter
import { getTierFromDifficulty } from '../../contexts/gameHelpers'; // Helper for styling

/**
 * GameHeader component displayed during active gameplay, replay, or tutorial.
 * Shows trial progress, pause/resume button (live), novice helper toggle (live/replay/tutorial),
 * and the FocusMeter. In replay mode, it displays Trial Count instead of username.
 *
 * Props:
 * - nBack: {number} Current n-back level.
 * - difficulty: {number} Current difficulty level (0.00-0.95) or seed difficulty for replay/tutorial.
 * - currentTrial: {number} Index of the current trial (0-based, -1 if not started).
 * - totalTrials: {number} Total number of trials in the session.
 * - gameState: {string} Current game state ('playing', 'paused', 'preparing', 'replay', 'tutorial', etc.).
 * - username?: {string|null} Optional username (passed but *not* displayed in header anymore).
 * - onPauseResume?: {function} Callback for Pause/Resume button clicks (live game).
 * - isNoviceLevel: {boolean} Indicates if the current level qualifies for helpers.
 * - showNoviceHelp: {boolean} Indicates if helpers are currently toggled ON.
 * - onToggleNoviceHelp?: {function} Callback for the Novice Help toggle button clicks.
 */
const GameHeader = ({
    nBack,
    difficulty,
    currentTrial,
    totalTrials,
    gameState,
    username = null, // Username prop is received but no longer displayed here
    onPauseResume, // Handler for Pause/Resume button
    isNoviceLevel, // Can helpers be shown for this level?
    showNoviceHelp, // Are helpers currently shown?
    onToggleNoviceHelp // Handler to toggle helpers
 }) => {

  // Determine if the helper toggle button should be visible.
  // Show if it's a novice-eligible level AND the game is active, paused, OR in replay/tutorial mode.
  const shouldShowHelperToggle = isNoviceLevel && ['playing', 'paused', 'replay', 'tutorial'].includes(gameState);

  // Determine the current difficulty tier for potential styling (mainly for reference, FocusMeter handles its visuals)
  const currentTier = getTierFromDifficulty(difficulty); // Keep this if needed for other potential styling
  console.log(`[GameHeader Render] gameState: ${gameState}, typeof onPauseResume: ${typeof onPauseResume}`);

  return (
    // Use Fragment to avoid adding extra DOM nodes
    <>
      {/* Top row container: Toggle, Center Info, Pause/Resume */}
      <div className="flex justify-between items-center mb-4">

        {/* --- Left Side: Helper Toggle Button or Placeholder --- */}
        {/* Container with fixed width for layout balance */}
        <div className="flex items-center space-x-2 w-24 sm:w-28 justify-start">
            {shouldShowHelperToggle && typeof onToggleNoviceHelp === 'function' ? (
                // Render the actual toggle button if conditions met and handler exists
                <button
                  onClick={onToggleNoviceHelp}
                  className={`px-2 py-1 text-xs rounded-md transition-colors duration-150 flex-shrink-0 ${
                    showNoviceHelp
                      ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' // Style when ON
                      : 'bg-gray-200 text-gray-600 hover:bg-gray-300' // Style when OFF
                  }`}
                  title={showNoviceHelp ? "Hide helper numbers/letters" : "Show helper numbers/letters"}
                  aria-pressed={showNoviceHelp}
                >
                  {showNoviceHelp ? 'Helpers On' : 'Helpers Off'}
                </button>
            ) : (
                // Render an empty div as a placeholder to maintain layout structure when toggle isn't shown
                <div className="w-20 h-7"></div> // Adjust size to roughly match button if needed
            )}
        </div>

        {/* --- Center: Trial Progress --- */}
        {/* Always display trial progress, regardless of game state */}
        <div className="font-bold text-lg text-center flex-1 px-2 truncate">
            <span>
                Trial: {currentTrial < 0 ? '-' : currentTrial + 1} / {totalTrials > 0 ? totalTrials : '?'}
            </span>
        </div>

        {/* --- Right Side: Pause/Resume Button or Placeholder --- */}
        {/* Container with fixed width for layout balance */}
        <div className="flex items-center space-x-2 w-24 sm:w-28 justify-end">
          {/* Pause Button: Show only when game state is 'playing' */}
          {gameState === 'playing' && typeof onPauseResume === 'function' && (
            <button
              className="px-3 py-1 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 text-sm shadow flex-shrink-0"
              onClick={onPauseResume}
              title="Pause Game (P)"
            >
              Pause/Exit
            </button>
          )}
           {/* Resume Button: Show only when game state is 'paused' */}
           {gameState === 'paused' && typeof onPauseResume === 'function' && (
            <button
              className="px-3 py-1 bg-green-500 text-white rounded-lg hover:bg-green-600 text-sm shadow flex-shrink-0"
              onClick={onPauseResume}
              title="Resume Game (P)"
            >
              Resume
            </button>
          )}
           {/* Placeholder: Show if not playing or paused (e.g., replay, tutorial, preparing) */}
           {!(gameState === 'playing' || gameState === 'paused') && (
                 <div className="w-16 h-7"></div> // Placeholder to maintain layout balance
           )}
        </div>

      </div>

      {/* --- Unified Focus Meter Component --- */}
      {/* Displays the N-Back level and difficulty visually */}
      {/* --- >>> MODIFICATION: Set showAdvancementInfo to false <<< --- */}
      <FocusMeter
        difficulty={difficulty}
        nBack={nBack}
        showAdvancementInfo={false} // Explicitly hide the text here
      />
      {/* --- >>> END MODIFICATION <<< --- */}

    </> // End Fragment
  );
};

export default GameHeader;